class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    // Check if this is mobile or desktop search
    this.isMobileSearch = this.dataset.mobileSearch === "true";
    this.isDesktopSearch = this.dataset.desktopSearch === "true";
    
    // Get container based on context
    if (this.isMobileSearch) {
      this.container = this.closest(".m-header__mobile-search") || this;
    } else if (this.isDesktopSearch) {
      this.container = this.closest(".m-search-form-desktop__container") || this;
    } else {
      this.container = document.querySelector("[data-search-popup]") || this;
    }

    this.domNodes = queryDomNodes(this.selectors, this.container || this);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    
    // Get results container based on context
    if (this.isMobileSearch) {
      this.searchPopupSearults = this.querySelector(".m-search-mobile__result");
    } else if (this.isDesktopSearch) {
      this.searchPopupSearults = this.querySelector(".m-search-desktop__result");
    } else {
      this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    }
    
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    let form;
    if (this.isMobileSearch) {
      form = this.querySelector("form.m-search-form-mobile");
    } else if (this.isDesktopSearch) {
      form = this.querySelector("form.m-search-form-desktop");
    } else {
      form = this.querySelector("form.m-search-form");
    }
    
    if (form) {
      form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    
    if (clear) {
      clear.addEventListener("click", this.onClearSearch.bind(this));
    }
    
    if (!this.isMobileSearch) {
      MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
    }

    // Close search when clicking outside (for mobile and desktop search)
    if (this.isMobileSearch || this.isDesktopSearch) {
      document.addEventListener("click", (e) => {
        if (this.isOpen && this.container) {
          // Check if click is outside the container
          if (!this.container.contains(e.target)) {
            this.close(true);
          }
        }
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    this.input.value = "";
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption;

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) searchFields += ",body";
    if (searchByBody) searchFields += ",tag";

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    let searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    fetch(`${searchURL}`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;
    
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    if (searchItemsWrapper && searchItemsWrapper.childElementCount > 0) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    const query = input.value;

    this.predictiveSearchResults.classList.remove("m:hidden");
    if (this.searchPopupSearults) {
      this.searchPopupSearults.classList.remove("m:hidden");
    }

    // Only show search count in popup, not in mobile or desktop
    if (!this.isMobileSearch && !this.isDesktopSearch && searchMessage && searchQuery) {
      const { resultsTitle } = searchMessage.dataset;
      searchQuery.textContent = query;

      if (results) {
        searchMessage.textContent = resultsTitle;
        if (moreResultIcon) {
          moreResultIcon.classList.add("m:hidden");
        }
      } else {
        searchMessage.textContent = searchMessage.dataset.resultsTitle;
        if (moreResultIcon) {
          moreResultIcon.classList.remove("m:hidden");
        }
      }
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    if (submit) {
      submit.style.visibility = show ? "hidden" : "visible";
    }
    if (loading) {
      loading.style.visibility = show ? "visible" : "hidden";
    }
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    if (this.searchPopupSearults) {
      this.searchPopupSearults.style.setProperty(
        "--search-result-max-height",
        `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
      );
    }
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    if (this.popularSearches) {
      this.popularSearches.classList.add("m:hidden");
    }
    if (this.searchCount) {
      this.searchCount.classList.remove("m:hidden");
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.add("m:hidden");
    }
    if (this.searchPopupSearults) {
      this.searchPopupSearults.classList.add("m:hidden");
    }
    if (this.popularSearches) {
      this.popularSearches.classList.remove("m:hidden");
    }
    if (this.searchCount) {
      this.searchCount.classList.add("m:hidden");
    }
  }

  getResultsMaxHeight() {
    const predictiveSearchElement = (this.isMobileSearch || this.isDesktopSearch)
      ? this 
      : document.querySelector("predictive-search");
    
    if (predictiveSearchElement) {
      this.resultsMaxHeight =
        window.innerHeight - predictiveSearchElement.getBoundingClientRect().bottom - 20;
      return this.resultsMaxHeight;
    }
    return 400; // Default height
  }
}

customElements.define("predictive-search", PredictiveSearch);
